import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { SortingState } from "@tanstack/react-table";
import {
  computePoFacets,
  deskOpenUsesLocalCache,
  facetOptionLabel,
  filterDeskOpenPurchaseOrders,
  matchesDueFilter,
  matchesLifecycleFilter,
  matchesPayAxisFilter,
  matchesReceiveAxisFilter,
  primaryDueBadge,
  sortDeskOpenBy,
  type DueFilter,
  type LifecycleFilter,
  type PayAxisFilter,
  type ReceiveAxisFilter,
} from "@icraftx/shared/v1";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  listPurchaseOrders,
  type PoListSort,
} from "@/shared/api/purchasing";
import {
  batchDocAssignees,
  batchDocTags,
  listTags,
  type AssigneeRow,
  type TagRow,
} from "@/shared/api/tags";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";
import {
  AdminFilters,
  adminFilterControlClass,
} from "@/modules/shell/AdminFilters";
import { AdminMoreFilters } from "@/modules/shell/AdminMoreFilters";
import { ActiveFilterChips } from "@/modules/shell/ActiveFilterChips";
import { SavedListViewsControl } from "@/modules/shell/SavedListViewsControl";
import { DocListLegend } from "@/modules/core/components/DocListLegend";
import { ORDER_LIFECYCLE_LEGEND } from "@/modules/core/components/lifecycle-status";
import type { PartyFilter } from "@/modules/core/list-cell-actions";
import {
  listSearchDebounceMs,
  paramOr,
  readPartyFilter,
  readQ,
  setOrDelete,
  writeLifecycleParam,
  writePartyFilter,
} from "@/modules/shell/list-url-filters";
import { useListUrlParams } from "@/modules/shell/use-list-url-params";
import { DataTable } from "@/modules/shell/data-table";
import {
  DataTableChrome,
  DeskListFilterReset,
} from "@/modules/shell/DataTableChrome";
import { useDeskListFilterReset } from "@/modules/shell/use-desk-list-filter-reset";
import {
  ListSearchStatus,
  listSearchOnlinePending,
} from "@/modules/shell/ListSearchStatus";
import { useListRowFlash } from "@/modules/shell/use-list-row-flash";
import { takeListReturn } from "@/modules/shell/list-return-state";
import { DeskListHeader } from "@/modules/shell/DeskListHeader";
import { DeskSubNav } from "@/modules/shell/DeskSubNav";
import { purchasingModuleTabs } from "@/modules/shell/desk-module-tabs";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { useDeskOpenCache } from "@/modules/shell/desk-sync/use-desk-open";
import {
  purchaseOrderColumns,
  type PoTableRow,
} from "./purchase-orders-columns";

type AssigneeFilter = "all" | "mine" | "unassigned";

const LIFECYCLES = ["all", "draft", "open", "void", "done"] as const;
const PAYS = ["all", "unpaid", "partial", "paid"] as const;
const RECEIVES = ["all", "none", "partial", "received"] as const;
const DUES = ["all", "this_week", "overdue", "none"] as const;
const ASSIGNEES = ["all", "mine", "unassigned"] as const;

const PAGE_SIZE = 50;
const SORTABLE: PoListSort[] = [
  "number",
  "supplierName",
  "total",
  "lifecycle",
  "dueAt",
  "docDate",
];

function sortingToApi(sorting: SortingState): {
  sort: PoListSort;
  sortDir: "asc" | "desc";
} {
  const first = sorting[0];
  if (first && SORTABLE.includes(first.id as PoListSort)) {
    return {
      sort: first.id as PoListSort,
      sortDir: first.desc ? "desc" : "asc",
    };
  }
  return { sort: "number", sortDir: "asc" };
}

export function PurchaseOrdersPage() {
  const { companySlug } = useParams();
  const location = useLocation();
  const { searchParams, patchParams, replaceAllParams } = useListUrlParams();

  const tagId = searchParams.get("tag") ?? "";
  const assigneeFilter = paramOr(
    searchParams,
    "assignee",
    ASSIGNEES,
    "all",
  ) as AssigneeFilter;
  const dueFilter = paramOr(searchParams, "due", DUES, "all") as DueFilter;
  const lifecycleFilter = paramOr(
    searchParams,
    "lifecycle",
    LIFECYCLES,
    "open",
  ) as LifecycleFilter;
  const payFilter = paramOr(searchParams, "pay", PAYS, "all") as PayAxisFilter;
  const receiveFilter = paramOr(
    searchParams,
    "receive",
    RECEIVES,
    "all",
  ) as ReceiveAxisFilter;
  const partyFilter = readPartyFilter(searchParams);

  const setTagId = (next: string) =>
    patchParams((p) => setOrDelete(p, "tag", next));
  const setAssigneeFilter = (next: AssigneeFilter) =>
    patchParams((p) => setOrDelete(p, "assignee", next));
  const setDueFilter = (next: DueFilter) =>
    patchParams((p) => setOrDelete(p, "due", next));
  const setLifecycleFilter = (next: LifecycleFilter) =>
    patchParams((p) => writeLifecycleParam(p, next));
  const setPayFilter = (next: PayAxisFilter) =>
    patchParams((p) => setOrDelete(p, "pay", next));
  const setReceiveFilter = (next: ReceiveAxisFilter) =>
    patchParams((p) => setOrDelete(p, "receive", next));
  const setPartyFilter = (next: PartyFilter | null) =>
    patchParams((p) => writePartyFilter(p, next));

  const [query, setQuery] = useState(() => readQ(searchParams));
  const [debouncedQ, setDebouncedQ] = useState(() => readQ(searchParams));
  const [sorting, setSorting] = useState<SortingState>([]);
  const { sort, sortDir } = sortingToApi(sorting);

  const useLocal = deskOpenUsesLocalCache({
    lifecycle: lifecycleFilter,
    tagId,
    assignee: assigneeFilter,
  });

  /** Keep search box in sync when saved views / back navigation rewrite URL. */
  useEffect(() => {
    const q = readQ(searchParams);
    setQuery((prev) => (prev === q ? prev : q));
    setDebouncedQ((prev) => (prev === q ? prev : q));
  }, [searchParams]);

  useEffect(() => {
    const ms = listSearchDebounceMs(useLocal);
    const t = window.setTimeout(() => {
      const q = query.trim();
      setDebouncedQ(q);
      if (readQ(searchParams) === q) return;
      patchParams((p) => setOrDelete(p, "q", q));
    }, ms);
    return () => window.clearTimeout(t);
  }, [query, patchParams, useLocal, searchParams]);

  const serverLifecycle =
    lifecycleFilter === "draft" ||
    lifecycleFilter === "open" ||
    lifecycleFilter === "void"
      ? lifecycleFilter
      : undefined;

  const searchQ = useLocal ? query.trim() : debouncedQ;

  const tagsQuery = useQuery({
    queryKey: ["tags", companySlug, "catalog"],
    enabled: Boolean(companySlug),
    queryFn: () => listTags(companySlug!),
  });

  /** Always hydrate desk-open for local list + cheap facets — plan/33+35 C. */
  const deskCacheQuery = useDeskOpenCache(companySlug, Boolean(companySlug));

  const listQuery = useInfiniteQuery({
    queryKey: queryKeys.purchaseOrders.list(companySlug ?? "", {
      tagId,
      assigneeFilter,
      q: debouncedQ,
      lifecycle: serverLifecycle ?? "all",
      limit: PAGE_SIZE,
      sort,
      sortDir,
    }),
    enabled: Boolean(companySlug) && !useLocal,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam, signal }) => {
      const list = await listPurchaseOrders(companySlug!, {
        q: debouncedQ || undefined,
        limit: PAGE_SIZE,
        cursor: pageParam,
        lifecycle: serverLifecycle,
        tagId: tagId || undefined,
        assignedToMe: assigneeFilter === "mine",
        unassigned: assigneeFilter === "unassigned",
        sort,
        sortDir,
        signal,
      });
      const ids = list.orders.map((o) => o.id);
      const [tagsBatch, assigneesBatch] = await Promise.all([
        batchDocTags(companySlug!, "purchase_order", ids),
        batchDocAssignees(companySlug!, "purchase_order", ids),
      ]);
      return {
        ...list,
        tagsByDoc: tagsBatch.byDoc,
        assigneesByDoc: assigneesBatch.byDoc,
      };
    },
    getNextPageParam: (last) =>
      last.hasMore && last.nextCursor ? last.nextCursor : undefined,
  });

  const localOrders = useMemo(() => {
    if (!useLocal || !deskCacheQuery.data) return [];
    const filtered = filterDeskOpenPurchaseOrders(
      deskCacheQuery.data.purchaseOrders,
      {
        q: searchQ,
        lifecycle: lifecycleFilter,
        pay: payFilter,
        receive: receiveFilter,
        partyId: partyFilter?.id,
        due: dueFilter,
      },
    );
    return sortDeskOpenBy(filtered, sort, sortDir, (r) => {
      if (sort === "supplierName") return r.supplierName.toLowerCase();
      if (sort === "total") return r.total;
      if (sort === "lifecycle") return r.lifecycle;
      if (sort === "docDate") {
        const raw = r.docDate;
        if (raw == null || raw === "") return "0000-00-00";
        return String(raw).slice(0, 10);
      }
      if (sort === "dueAt") {
        const raw = r.paymentDueOn;
        if (raw == null || raw === "") return "9999-12-31";
        return String(raw).slice(0, 10);
      }
      return r.number.toLowerCase();
    });
  }, [
    useLocal,
    deskCacheQuery.data,
    searchQ,
    lifecycleFilter,
    payFilter,
    receiveFilter,
    partyFilter?.id,
    dueFilter,
    sort,
    sortDir,
  ]);

  const localMetaQuery = useQuery({
    queryKey: [
      "deskOpen",
      companySlug,
      "poMeta",
      localOrders.map((o) => o.id).join(","),
    ],
    enabled: Boolean(companySlug) && useLocal && localOrders.length > 0,
    queryFn: async () => {
      const ids = localOrders.map((o) => o.id);
      const [tagsBatch, assigneesBatch] = await Promise.all([
        batchDocTags(companySlug!, "purchase_order", ids),
        batchDocAssignees(companySlug!, "purchase_order", ids),
      ]);
      return {
        tagsByDoc: tagsBatch.byDoc,
        assigneesByDoc: assigneesBatch.byDoc,
      };
    },
  });

  const { flashId, flashRow } = useListRowFlash();
  const listPath = companySlug
    ? `/c/${companySlug}/purchasing/orders${location.search}`
    : "";
  const listReturnHandled = useRef(false);

  const columns = useMemo(
    () =>
      companySlug
        ? purchaseOrderColumns(companySlug, {
            partyFilter,
            setPartyFilter,
            payFilter,
            setPayFilter,
            receiveFilter,
            setReceiveFilter,
            dueFilter,
            setDueFilter,
            tagId,
            setTagId,
            onFilterFromRow: flashRow,
            listPath,
          })
        : [],
    // setters are stable via patchParams
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      companySlug,
      partyFilter,
      payFilter,
      receiveFilter,
      dueFilter,
      tagId,
      flashRow,
      listPath,
    ],
  );

  const loading = useLocal
    ? deskCacheQuery.isPending
    : listQuery.isPending;

  useEffect(() => {
    if (!companySlug || loading || listReturnHandled.current) return;
    listReturnHandled.current = true;
    const id = takeListReturn(`/c/${companySlug}/purchasing/orders`);
    if (id) flashRow(id);
  }, [companySlug, loading, flashRow]);

  const errorMsg = queryErrorMessage(
    (useLocal ? deskCacheQuery.error : listQuery.error) ?? tagsQuery.error,
    (useLocal ? deskCacheQuery.isError : listQuery.isError) ||
      tagsQuery.isError,
  );
  useAuthRedirectOnQueryError(errorMsg);

  const filtersActive =
    Boolean(query.trim()) ||
    Boolean(tagId) ||
    Boolean(partyFilter) ||
    assigneeFilter !== "all" ||
    dueFilter !== "all" ||
    lifecycleFilter !== "open" ||
    payFilter !== "all" ||
    receiveFilter !== "all";
  const filtersUnrestricted =
    !query.trim() &&
    !tagId &&
    !partyFilter &&
    assigneeFilter === "all" &&
    dueFilter === "all" &&
    lifecycleFilter === "all" &&
    payFilter === "all" &&
    receiveFilter === "all";
  const filterReset = useDeskListFilterReset({
    patchParams,
    setQuery,
    canResetToDefault: filtersActive,
    canClearAll: !filtersUnrestricted,
    clearAllSetsLifecycleAll: true,
  });
  const searchingOnline = listSearchOnlinePending({
    query,
    debouncedQ,
    isFetching: listQuery.isFetching,
  });
  const moreFiltersActive =
    (tagId ? 1 : 0) +
    (assigneeFilter !== "all" ? 1 : 0) +
    (dueFilter !== "all" ? 1 : 0) +
    (receiveFilter !== "all" ? 1 : 0);

  const facets = useMemo(() => {
    if (!deskCacheQuery.data) return null;
    return computePoFacets(deskCacheQuery.data.purchaseOrders, {
      q: searchQ,
      lifecycle: lifecycleFilter,
      pay: payFilter,
      receive: receiveFilter,
      partyId: partyFilter?.id,
      due: dueFilter,
    });
  }, [
    deskCacheQuery.data,
    searchQ,
    lifecycleFilter,
    payFilter,
    receiveFilter,
    partyFilter?.id,
    dueFilter,
  ]);

  if (!companySlug) return null;

  const orders = useLocal
    ? localOrders
    : (listQuery.data?.pages.flatMap((p) => p.orders) ?? []);
  const tagsByDoc: Record<string, TagRow[]> = {};
  const assigneesByDoc: Record<string, AssigneeRow[]> = {};
  if (useLocal) {
    Object.assign(tagsByDoc, localMetaQuery.data?.tagsByDoc ?? {});
    Object.assign(assigneesByDoc, localMetaQuery.data?.assigneesByDoc ?? {});
  } else {
    for (const page of listQuery.data?.pages ?? []) {
      Object.assign(tagsByDoc, page.tagsByDoc);
      Object.assign(assigneesByDoc, page.assigneesByDoc ?? {});
    }
  }
  const catalog = tagsQuery.data?.tags ?? [];
  const total = useLocal
    ? localOrders.length
    : listQuery.data?.pages[0]?.total;
  const error = errorMsg && errorMsg !== "Not signed in" ? errorMsg : null;

  const withMeta = (o: (typeof orders)[number]): PoTableRow => ({
    ...o,
    tags: tagsByDoc[o.id] ?? [],
    assignees: assigneesByDoc[o.id] ?? [],
  });

  const visible: PoTableRow[] = useLocal
    ? localOrders.map(withMeta)
    : orders
        .filter((o) => {
          if (partyFilter && o.supplierId !== partyFilter.id) return false;
          if (
            !matchesLifecycleFilter(o.lifecycle, lifecycleFilter, {
              pay: o.pay,
              receive: o.receive,
            })
          ) {
            return false;
          }
          if (!matchesPayAxisFilter(o.pay, payFilter)) return false;
          if (!matchesReceiveAxisFilter(o.receive, receiveFilter)) {
            return false;
          }
          return matchesDueFilter(
            primaryDueBadge({
              paymentDueOn: o.paymentDueOn,
              expectedOn: o.expectedOn,
            }),
            dueFilter,
          );
        })
        .map(withMeta);

  return (
    <PageFrame>
      <DataTableChrome>
      <DeskListFilterReset {...filterReset} />
      <DeskListHeader
        title="Purchase orders"
        description="Buy → receive → pay suppliers"
        tabs={
          <DeskSubNav
            placement="header"
            items={purchasingModuleTabs(companySlug)}
            aria-label="Purchasing views"
          />
        }
        primaryAction={
          <Button asChild>
            <Link
              to={`/c/${companySlug}/purchasing/orders/new`}
              className="no-underline"
            >
              New PO
            </Link>
          </Button>
        }
      />

      <AdminFilters>
        <input
          className={`${adminFilterControlClass} min-w-[12rem] flex-1`}
          placeholder="Search number, supplier, ref…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={loading && orders.length === 0}
        />
        <select
          className={adminFilterControlClass}
          value={lifecycleFilter}
          onChange={(e) =>
            setLifecycleFilter(e.target.value as LifecycleFilter)
          }
        >
          <option value="all">
            {facetOptionLabel("All lifecycle", facets?.lifecycle.all)}
          </option>
          <option value="draft">
            {facetOptionLabel("Draft", facets?.lifecycle.draft)}
          </option>
          <option value="open">
            {facetOptionLabel("Posted", facets?.lifecycle.open)}
          </option>
          <option value="void">
            {facetOptionLabel("Void", facets?.lifecycle.void)}
          </option>
          <option value="done">
            {facetOptionLabel("Done", facets?.lifecycle.done)}
          </option>
        </select>
        <select
          className={adminFilterControlClass}
          value={payFilter}
          onChange={(e) => setPayFilter(e.target.value as PayAxisFilter)}
        >
          <option value="all">
            {facetOptionLabel("All pay", facets?.pay.all)}
          </option>
          <option value="unpaid">
            {facetOptionLabel("Unpaid", facets?.pay.unpaid)}
          </option>
          <option value="partial">
            {facetOptionLabel("Partial pay", facets?.pay.partial)}
          </option>
          <option value="paid">
            {facetOptionLabel("Paid", facets?.pay.paid)}
          </option>
        </select>
        <AdminMoreFilters activeCount={moreFiltersActive}>
          <select
            className={adminFilterControlClass}
            value={receiveFilter}
            onChange={(e) =>
              setReceiveFilter(e.target.value as ReceiveAxisFilter)
            }
          >
            <option value="all">
              {facetOptionLabel("All receive", facets?.fulfill.all)}
            </option>
            <option value="none">
              {facetOptionLabel("Not received", facets?.fulfill.none)}
            </option>
            <option value="partial">
              {facetOptionLabel("Partial recv", facets?.fulfill.partial)}
            </option>
            <option value="received">
              {facetOptionLabel("Received", facets?.fulfill.received)}
            </option>
          </select>
          <select
            className={adminFilterControlClass}
            value={tagId}
            onChange={(e) => setTagId(e.target.value)}
          >
            <option value="">All tags</option>
            {catalog.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <select
            className={adminFilterControlClass}
            value={assigneeFilter}
            onChange={(e) =>
              setAssigneeFilter(e.target.value as AssigneeFilter)
            }
          >
            <option value="all">All assignees</option>
            <option value="mine">Assigned to me</option>
            <option value="unassigned">Unassigned</option>
          </select>
          <select
            className={adminFilterControlClass}
            value={dueFilter}
            onChange={(e) => setDueFilter(e.target.value as DueFilter)}
          >
            <option value="all">
              {facetOptionLabel("All dues", facets?.due.all)}
            </option>
            <option value="this_week">
              {facetOptionLabel("Due this week", facets?.due.this_week)}
            </option>
            <option value="overdue">
              {facetOptionLabel("Overdue", facets?.due.overdue)}
            </option>
            <option value="none">
              {facetOptionLabel("No due date", facets?.due.none)}
            </option>
          </select>
        </AdminMoreFilters>
        <SavedListViewsControl
          listId="purchase-orders"
          companySlug={companySlug}
          currentQs={searchParams.toString()}
          onApplyQs={(qs) => {
            replaceAllParams(qs);
            const q = readQ(new URLSearchParams(qs));
            setQuery(q);
            setDebouncedQ(q);
          }}
        />
        <ActiveFilterChips
          chips={[
            ...(partyFilter
              ? [
                  {
                    key: "party",
                    label: `Supplier: ${partyFilter.name}`,
                    onClear: () => setPartyFilter(null),
                  },
                ]
              : []),
            ...(lifecycleFilter !== "open"
              ? [
                  {
                    key: "lifecycle",
                    label:
                      lifecycleFilter === "all"
                        ? "All lifecycle"
                        : lifecycleFilter === "done"
                          ? "Done"
                          : lifecycleFilter[0]!.toUpperCase() +
                            lifecycleFilter.slice(1),
                    onClear: () => setLifecycleFilter("open"),
                  },
                ]
              : []),
            ...(payFilter !== "all"
              ? [
                  {
                    key: "pay",
                    label:
                      payFilter === "unpaid"
                        ? "Unpaid"
                        : payFilter === "partial"
                          ? "Partial pay"
                          : "Paid",
                    onClear: () => setPayFilter("all"),
                  },
                ]
              : []),
            ...(receiveFilter !== "all"
              ? [
                  {
                    key: "receive",
                    label:
                      receiveFilter === "none"
                        ? "Not received"
                        : receiveFilter === "partial"
                          ? "Partial recv"
                          : "Received",
                    onClear: () => setReceiveFilter("all"),
                  },
                ]
              : []),
            ...(tagId
              ? [
                  {
                    key: "tag",
                    label: `Tag: ${catalog.find((t) => t.id === tagId)?.name ?? tagId}`,
                    onClear: () => setTagId(""),
                  },
                ]
              : []),
            ...(assigneeFilter !== "all"
              ? [
                  {
                    key: "assignee",
                    label:
                      assigneeFilter === "mine"
                        ? "Assigned to me"
                        : "Unassigned",
                    onClear: () => setAssigneeFilter("all"),
                  },
                ]
              : []),
            ...(dueFilter !== "all"
              ? [
                  {
                    key: "due",
                    label:
                      dueFilter === "this_week"
                        ? "Due this week"
                        : dueFilter === "overdue"
                          ? "Overdue"
                          : "No due date",
                    onClear: () => setDueFilter("all"),
                  },
                ]
              : []),
          ]}
        />
      </AdminFilters>

      <DocListLegend
        kinds={ORDER_LIFECYCLE_LEGEND}
        showFulfillmentComplete
      />

      <ListSearchStatus online={!useLocal} searching={searchingOnline} />

      {!loading && orders.length > 0 && total != null ? (
        <p className="mt-2 text-xs text-[var(--color-muted)]">
          Showing {visible.length}
          {total > orders.length ? ` · loaded ${orders.length} of ${total}` : ""}
        </p>
      ) : null}

      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}
      {loading && orders.length === 0 && (
        <Alert variant="muted" className="mt-8">
          Loading purchase orders…
        </Alert>
      )}

      {!loading && visible.length === 0 && !error ? (
        <div className="mt-8">
          <EmptyState
            title={
              filtersActive
                ? "No purchase orders match"
                : "No purchase orders yet"
            }
            description={
              filtersActive
                ? "Try clearing search or filters — or Load more if axis filters are on."
                : "Create a PO to receive stock, then pay the supplier."
            }
            action={
              filtersActive ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => filterReset.onClearAll()}
                >
                  Clear filters
                </Button>
              ) : (
                <div className="flex flex-wrap justify-center gap-2">
                  <Button asChild size="sm">
                    <Link
                      to={`/c/${companySlug}/purchasing/orders/new`}
                      className="no-underline"
                    >
                      New PO
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="sm">
                    <Link
                      to={`/c/${companySlug}/contacts?role=supplier`}
                      className="no-underline"
                    >
                      Suppliers
                    </Link>
                  </Button>
                </div>
              )
            }
          />
        </div>
      ) : null}

      {visible.length > 0 ? (
        <>
          <DataTable
            listId="purchase-orders"
            companySlug={companySlug}
            columns={columns}
            data={visible}
            getRowId={(r) => r.id}
            sorting={sorting}
            onSortingChange={setSorting}
            highlightedRowId={flashId}
            defaultVisibility={{ tags: false, assignees: false }}
            onRefresh={() =>
              void (useLocal
                ? deskCacheQuery.refetch()
                : listQuery.refetch())
            }
          />
          {!useLocal && listQuery.hasNextPage ? (
            <div className="mt-4 flex justify-center">
              <Button
                type="button"
                variant="outline"
                disabled={listQuery.isFetchingNextPage}
                pending={listQuery.isFetchingNextPage}
                pendingLabel="Loading more…"
                onClick={() => void listQuery.fetchNextPage()}
              >
                Load more
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
          </DataTableChrome>
    </PageFrame>
  );
}
